<script setup lang="ts">
/*
 * Column chooser — a small Headless UI popover with one checkbox per column
 * to show / hide it in a list-view table, plus "Reset". Controlled: it reads
 * `hiddenIds` and emits `change` / `reset`; persistence belongs to
 * `useColumnPrefs` (`@/composables/useColumnPrefs`).
 *
 *   const prefs = useColumnPrefs('people.directory', columns);
 *   <ColumnChooser :columns="columns" :hidden-ids="prefs.hiddenIds.value"
 *     @change="prefs.setVisible" @reset="prefs.reset" />
 *
 * Required columns render checked + disabled. For the grouped, reorderable
 * drawer, see `ColumnCustomizer`.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { Columns3 } from 'lucide-vue-next';

import Checkbox from './Checkbox.vue';
import Button from './Button.vue';
import type { ChooserColumn } from '@/composables/useColumnPrefs';

const props = withDefaults(
  defineProps<{
    /** Every column the table can show. */
    columns: readonly ChooserColumn[];
    /** Ids currently hidden. */
    hiddenIds?: readonly string[];
  }>(),
  { hiddenIds: () => [] },
);

const emit = defineEmits<{
  /** A column was switched on or off. */
  change: [id: string, visible: boolean];
  /** "Reset" was pressed. */
  reset: [];
}>();

const { t } = useI18n();

const hidden = computed(() => new Set(props.hiddenIds));
const shownCount = computed(() => props.columns.filter((c) => !hidden.value.has(c.id)).length);
</script>

<template>
  <Popover class="relative inline-flex" data-testid="column-chooser">
    <PopoverButton
      type="button"
      class="inline-flex items-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-3 py-1.5 text-md font-medium text-app-fg hover:bg-slate-50 focus:outline-none focus-visible:ring-3 focus-visible:ring-primary/20"
      :aria-label="
        t('v2.ui.column-chooser.button-aria', { shown: shownCount, total: columns.length })
      "
      data-testid="column-chooser-button"
    >
      <Columns3 class="h-4 w-4" aria-hidden="true" />
      <span>{{ t('v2.ui.column-chooser.button') }}</span>
    </PopoverButton>

    <PopoverPanel
      class="absolute top-full right-0 z-[61] mt-1 w-64 rounded-xl border border-app-border bg-app-surface p-3 shadow-lg"
      data-testid="column-chooser-panel"
    >
      <fieldset>
        <legend class="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">
          {{ t('v2.ui.column-chooser.title') }}
        </legend>
        <ul class="max-h-72 list-none space-y-1.5 overflow-y-auto p-0">
          <li v-for="col in columns" :key="col.id">
            <Checkbox
              :model-value="col.required || !hidden.has(col.id)"
              :disabled="col.required"
              :label="col.label"
              :data-testid="`column-chooser-opt-${col.id}`"
              @update:model-value="(v: boolean) => emit('change', col.id, v)"
            />
            <span v-if="col.required" class="sr-only">
              {{ t('v2.ui.column-chooser.required') }}
            </span>
          </li>
        </ul>
      </fieldset>
      <div class="mt-3 flex items-center justify-between border-t border-app-border-soft pt-2">
        <span class="text-[11.5px] text-app-muted" role="status" aria-live="polite">
          {{ t('v2.ui.column-chooser.summary', { shown: shownCount, total: columns.length }) }}
        </span>
        <Button
          variant="secondary"
          size="xs"
          data-testid="column-chooser-reset"
          @click="emit('reset')"
        >
          {{ t('v2.ui.column-chooser.reset') }}
        </Button>
      </div>
    </PopoverPanel>
  </Popover>
</template>
